import { Link, useParams } from "wouter";
import { SEOHead, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { BLOG_POSTS, BlogSection, getRelatedPosts } from "@/lib/blog-data";
import logoImg from "@assets/Untitled_design_(9)_1773925932166.png";
import logoDarkImg from "@assets/Untitled_design_(10)_1773926263699.png";
import { useTheme } from "@/components/theme-provider";
import { Button } from "@/components/ui/button";
import { ChevronRight, Clock, Calendar, User, ArrowRightLeft } from "lucide-react";
import { useEffect } from "react";

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function Section({ section }: { section: BlogSection }) {
  switch (section.type) {
    case "h2":
      return (
        <h2 className="text-2xl sm:text-3xl font-bold mt-10 mb-4 leading-snug">
          {section.content as string}
        </h2>
      );
    case "h3":
      return (
        <h3 className="text-xl font-semibold mt-8 mb-3 leading-snug">
          {section.content as string}
        </h3>
      );
    case "p":
      return (
        <p
          className="text-base sm:text-lg leading-relaxed text-foreground/90 mb-4 [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 [&_a]:hover:text-primary/80"
          dangerouslySetInnerHTML={{ __html: section.content as string }}
        />
      );
    case "ul":
      return (
        <ul className="list-disc pl-6 mb-6 space-y-2">
          {(section.content as string[]).map((item, i) => (
            <li
              key={i}
              className="text-base sm:text-lg leading-relaxed text-foreground/90 [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 [&_a]:hover:text-primary/80"
              dangerouslySetInnerHTML={{ __html: item }}
            />
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol className="list-decimal pl-6 mb-6 space-y-2">
          {(section.content as string[]).map((item, i) => (
            <li
              key={i}
              className="text-base sm:text-lg leading-relaxed text-foreground/90 [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 [&_a]:hover:text-primary/80"
              dangerouslySetInnerHTML={{ __html: item }}
            />
          ))}
        </ol>
      );
    case "callout":
      return (
        <div className="border-l-4 border-primary bg-primary/5 rounded-r-xl p-5 my-6">
          <p className="text-base sm:text-lg leading-relaxed text-foreground/90">
            {section.content as string}
          </p>
        </div>
      );
    case "cta":
      return (
        <div className="bg-primary/5 border border-primary/20 rounded-2xl p-8 my-10 text-center">
          <h3 className="text-xl font-bold mb-3">{section.heading}</h3>
          <p className="text-muted-foreground mb-6 max-w-lg mx-auto">{section.content as string}</p>
          <Button size="lg" asChild>
            <Link href="/login">Get started free</Link>
          </Button>
        </div>
      );
    default:
      return null;
  }
}

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const { theme } = useTheme();
  const logo = theme === "dark" ? logoDarkImg : logoImg;

  const post = BLOG_POSTS.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Article not found</h1>
          <Link href="/blog">
            <Button>Back to resources</Button>
          </Link>
        </div>
      </div>
    );
  }

  const postUrl = `https://thepropertool.com/blog/${post.slug}`;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": postUrl,
    "headline": post.title,
    "description": post.metaDescription,
    "image": {
      "@type": "ImageObject",
      "url": post.coverImage ?? "https://thepropertool.com/logo.png",
      "width": 1200,
      "height": 630,
    },
    "datePublished": post.publishedDate,
    "dateModified": post.modifiedDate,
    "author": post.author
      ? {
          "@type": "Person",
          "name": post.author.name,
          ...(post.author.url ? { "url": post.author.url } : {}),
        }
      : {
          "@type": "Organization",
          "name": "The Proper Tool",
          "url": "https://thepropertool.com",
        },
    "publisher": {
      "@type": "Organization",
      "name": "The Proper Tool",
      "url": "https://thepropertool.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://thepropertool.com/logo.png",
        "width": 1200,
        "height": 630,
      },
    },
    "url": postUrl,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": postUrl,
    },
    "isPartOf": {
      "@type": "ItemList",
      "url": "https://thepropertool.com/blog",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://thepropertool.com",
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog & Guides",
        "item": "https://thepropertool.com/blog",
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": post.title,
        "item": postUrl,
      },
    ],
  };

  const faqSchema = post.faqs.length > 0
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": post.faqs.map((faq) => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.a,
          },
        })),
      }
    : null;

  const schemas = [articleSchema, breadcrumbSchema, ...(faqSchema ? [faqSchema] : []), ORGANIZATION_SCHEMA];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title={post.metaTitle}
        description={post.metaDescription}
        canonical={`/blog/${post.slug}`}
        ogType="article"
        ogImage={post.coverImage}
        articlePublished={post.publishedDate}
        articleModified={post.modifiedDate}
        schema={schemas}
      />

      <header className="border-b border-border/40 bg-background/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link href="/">
            <img src={logo} alt="The Proper Tool" className="h-8 w-auto" />
          </Link>
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-4">
              <Link href="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Home</Link>
              <Link href="/blog" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Blog &amp; Guides</Link>
            </div>
            <Button size="sm" asChild>
              <Link href="/login">Sign in</Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
          <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
          <ChevronRight className="h-4 w-4" />
          <Link href="/blog" className="hover:text-foreground transition-colors">Blog &amp; Guides</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="truncate max-w-xs">{post.category}</span>
        </div>

        <div className="mb-2">
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">{post.category}</span>
        </div>

        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6" data-testid="text-article-title">
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center gap-5 text-sm text-muted-foreground mb-10 pb-8 border-b border-border/60">
          {post.author && (
            <span className="flex items-center gap-1.5">
              <User className="h-4 w-4" />
              {post.author.name}
            </span>
          )}
          <span className="flex items-center gap-1.5">
            <Calendar className="h-4 w-4" />
            {formatDate(post.publishedDate)}
          </span>
          <span className="flex items-center gap-1.5">
            <Clock className="h-4 w-4" />
            {post.readTime}
          </span>
        </div>

        <article className="prose-spacing">
          <p className="text-xl sm:text-2xl text-muted-foreground leading-relaxed mb-8 font-medium">
            {post.excerpt}
          </p>

          {post.sections.map((section, i) => (
            <Section key={i} section={section} />
          ))}
        </article>

        {post.author && post.author.bio && (
          <section className="mt-12 pt-10 border-t border-border/60">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xl select-none">
                {post.author.name.split(" ").map((n) => n[0]).join("")}
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">Written by</p>
                <p className="font-semibold text-foreground">{post.author.name}</p>
                {post.author.role && (
                  <p className="text-xs text-muted-foreground mt-0.5">{post.author.role}</p>
                )}
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{post.author.bio}</p>
              </div>
            </div>
          </section>
        )}

        {(() => {
          const relatedPosts = getRelatedPosts(post);
          return relatedPosts.length > 0 ? (
            <section className="mt-12 pt-10 border-t border-border/60">
              <h2 className="text-xl font-bold mb-5">You might also like</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {relatedPosts.map((related) => (
                <Link
                  key={related.slug}
                  href={`/blog/${related.slug}`}
                  className="group flex items-start gap-3 rounded-xl border border-border p-4 hover:border-primary/40 hover:bg-primary/5 transition-colors"
                >
                  <ChevronRight className="h-4 w-4 flex-shrink-0 mt-1 text-primary group-hover:translate-x-0.5 transition-transform" />
                  <span className="text-sm font-medium leading-snug group-hover:text-primary transition-colors">
                    {related.title}
                  </span>
                </Link>
              ))}
              </div>
            </section>
          ) : null;
        })()}

        {post.relatedGuides && post.relatedGuides.length > 0 && (
          <section className="mt-12 pt-10 border-t border-border/60">
            <h2 className="text-xl font-bold mb-2">Related guides</h2>
            <p className="text-sm text-muted-foreground mb-5">In-depth guides on topics covered in this article.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {post.relatedGuides.map((guide) => (
                <Link
                  key={guide.href}
                  href={guide.href}
                  className="group flex items-start gap-3 rounded-xl border border-border p-4 hover:border-primary/40 hover:bg-primary/5 transition-colors"
                >
                  <ChevronRight className="h-4 w-4 flex-shrink-0 mt-1 text-primary group-hover:translate-x-0.5 transition-transform" />
                  <span className="text-sm font-medium leading-snug group-hover:text-primary transition-colors">
                    {guide.title}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {post.relatedComparisons && post.relatedComparisons.length > 0 && (
          <section className="mt-12 pt-10 border-t border-border/60">
            <h2 className="text-xl font-bold mb-2">Compare alternatives</h2>
            <p className="text-sm text-muted-foreground mb-5">See how The Proper Tool stacks up against other popular platforms.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {post.relatedComparisons.map((comparison) => (
                <Link
                  key={comparison.slug}
                  href={`/alternatives/${comparison.slug}`}
                  className="group flex items-start gap-3 rounded-xl border border-border p-4 hover:border-primary/40 hover:bg-primary/5 transition-colors"
                >
                  <ArrowRightLeft className="h-4 w-4 flex-shrink-0 mt-1 text-primary" />
                  <span className="text-sm font-medium leading-snug group-hover:text-primary transition-colors">
                    {comparison.title}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {post.faqs.length > 0 && (
          <section className="mt-12 pt-10 border-t border-border/60">
            <h2 className="text-2xl font-bold mb-6">Frequently asked questions</h2>
            <div className="space-y-6">
              {post.faqs.map((faq, i) => (
                <div key={i} className="border border-border rounded-xl p-6">
                  <h3 className="font-semibold text-lg mb-2 leading-snug">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="mt-12 pt-8 border-t border-border/60">
          <div className="bg-primary/5 border border-primary/20 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold mb-3">Quote faster with The Proper Tool</h2>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
              Builder quoting software built for UK tradespeople. Generate a professional construction quote from drawings or a job description in under 10 minutes.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">Start free, no card needed</Link>
            </Button>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link href="/blog" className="text-sm text-primary hover:underline flex items-center gap-1 justify-center">
            <ChevronRight className="h-4 w-4 rotate-180" />
            Back to all resources
          </Link>
        </div>
      </main>

      <footer className="border-t border-border/40 mt-16 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} The Proper Tool. Built for UK builders.</p>
          <div className="flex gap-6">
            <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link href="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <Link href="/blog" className="hover:text-foreground transition-colors">Blog &amp; Guides</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
